import type { PaymentInterval } from '../types/billing';

/**
 * Plan of a standing invoice („Dauerrechnung“, FAFD-27): the service period in whole months
 * (`YYYY-MM-DD`) and how often the customer pays. Chosen when the invoice is created — the object
 * only says that it is billed by standing invoice.
 */
export interface StandingPlan {
  from: string;
  to: string;
  interval: PaymentInterval;
}

/** i18n keys (namespace `invoices`) of the plan's field errors. */
export interface StandingPlanErrors {
  from?: string;
  to?: string;
}

const parseIsoDate = (value?: string | null): { year: number; month: number; day: number } | null => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
};

/** Number of days of a month (1-based), leap years included. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function isFirstOfMonth(value?: string | null): boolean {
  return parseIsoDate(value)?.day === 1;
}

export function isLastOfMonth(value?: string | null): boolean {
  const date = parseIsoDate(value);
  return !!date && date.day === daysInMonth(date.year, date.month);
}

/** Default plan: the next calendar year, paid monthly. */
export function defaultStandingPlan(today: Date = new Date()): StandingPlan {
  const year = today.getFullYear() + 1;
  return { from: `${year}-01-01`, to: `${year}-12-31`, interval: 'monthly' };
}

/** Longest service period of one standing invoice — two service years, like the backend. */
export const MAX_STANDING_MONTHS = 24;

/** Months from the start month to the end month, both included. */
export function standingPlanMonths(plan: Pick<StandingPlan, 'from' | 'to'>): number {
  const from = parseIsoDate(plan.from);
  const to = parseIsoDate(plan.to);
  if (!from || !to) return 0;
  return (to.year - from.year) * 12 + to.month - from.month + 1;
}

/**
 * Mirrors the backend rule `invoices.standingPeriodInvalid`: the period starts on the 1st and ends
 * on the last day of a month, the end not before the start, at most 24 months.
 */
export function validateStandingPlan(plan: Pick<StandingPlan, 'from' | 'to'>): StandingPlanErrors {
  const errors: StandingPlanErrors = {};
  if (!parseIsoDate(plan.from)) errors.from = 'standingPlan.errors.dateRequired';
  else if (!isFirstOfMonth(plan.from)) errors.from = 'standingPlan.errors.startNotFirstOfMonth';

  if (!parseIsoDate(plan.to)) errors.to = 'standingPlan.errors.dateRequired';
  else if (!isLastOfMonth(plan.to)) errors.to = 'standingPlan.errors.endNotLastOfMonth';
  // why: ISO dates compare lexicographically.
  else if (!errors.from && plan.to < plan.from) errors.to = 'standingPlan.errors.endBeforeStart';
  else if (!errors.from && standingPlanMonths(plan) > MAX_STANDING_MONTHS) errors.to = 'standingPlan.errors.tooLong';

  return errors;
}

export function isStandingPlanValid(plan: Pick<StandingPlan, 'from' | 'to'>): boolean {
  const errors = validateStandingPlan(plan);
  return !errors.from && !errors.to;
}

